import { GoodRoad } from '@/components/roadmap/data-model'
import { parseRoadMap } from '@/components/roadmap/utils'
import { getGoodRoadName } from '@/utils/constants'
import { Table } from 'vue'

export interface GoodRoadTableEntry {
    table: Table
    goodRoads: GoodRoad[]
    goodRoadName: string
}

export function getTableGoodRoads(table: Table): GoodRoad[] {
    const resultList = table.expand?.resultList?.join('#') || ''
    return parseRoadMap(resultList).goodRoads
}

export function formatGoodRoadName(id: number, gameTypeNo: string): string {
    return getGoodRoadName(id, gameTypeNo)
}

export function buildGoodRoadEntry(table: Table): GoodRoadTableEntry | null {
    const goodRoads = getTableGoodRoads(table)
    if (!goodRoads.length) return null

    return buildTableEntry(table, goodRoads)
}

export function buildTableEntry(
    table: Table,
    goodRoads: GoodRoad[] = getTableGoodRoads(table)
): GoodRoadTableEntry {
    return {
        table,
        goodRoads,
        goodRoadName: goodRoads.length
            ? formatGoodRoadName(goodRoads[0].id, table.gameTypeNo)
            : ''
    }
}

export function filterGoodRoadTables(tables: Table[]): GoodRoadTableEntry[] {
    return tables
        .map(buildGoodRoadEntry)
        .filter((entry): entry is GoodRoadTableEntry => entry !== null)
}

export type TableSortMode = 'goodRoad' | 'table'

function compareTableOrder(a: Table, b: Table): number {
    const aNo = Number(a.tableNo)
    const bNo = Number(b.tableNo)
    if (Number.isFinite(aNo) && Number.isFinite(bNo) && aNo !== bNo) {
        return aNo - bNo
    }
    if (String(a.tableNo) !== String(b.tableNo)) {
        return String(a.tableNo).localeCompare(String(b.tableNo), 'zh')
    }
    return a.id - b.id
}

export function sortTables(tables: Table[], mode: TableSortMode): Table[] {
    if (mode === 'table') {
        return [...tables].sort(compareTableOrder)
    }

    return buildPanelListData(tables).map(entry => entry.table)
}

/**
 * 长路 > 单跳 > 一庄两闲 > 长路转单跳 > 逢庄跳 > 逢庄连.
 * The opposite side of each pattern shares that rank.
 * 长路 is then ordered by streak descending (6连闲 before 5连闲).
 * Equal rank and streak keep hall order. Tables with no good road follow.
 */
const GOOD_ROAD_CATEGORY_RANK: Readonly<Record<number, number>> = {
    1: 0, // 长庄 / 长龙
    2: 0, // 长闲 / 长虎
    3: 1, // 大路单跳
    4: 2, // 一庄两闲 / 一龙两虎
    5: 2, // 一闲两庄 / 一虎两龙
    6: 3, // 长路转单跳
    7: 4, // 逢庄跳 / 逢龙跳
    8: 4, // 逢闲跳 / 逢虎跳
    9: 5, // 逢庄连 / 逢龙连
    10: 5 // 逢闲连 / 逢虎连
}

const LONG_ROAD_RANK = 0

function compareGoodRoadSort(
    a: GoodRoadTableEntry,
    b: GoodRoadTableEntry,
    aIndex: number,
    bIndex: number
): number {
    const aRoad = a.goodRoads[0]
    const bRoad = b.goodRoads[0]
    const aHas = aRoad ? 1 : 0
    const bHas = bRoad ? 1 : 0
    if (aHas !== bHas) return bHas - aHas
    if (!aRoad || !bRoad) return aIndex - bIndex

    const aRank = GOOD_ROAD_CATEGORY_RANK[aRoad.id] ?? Number.MAX_SAFE_INTEGER
    const bRank = GOOD_ROAD_CATEGORY_RANK[bRoad.id] ?? Number.MAX_SAFE_INTEGER
    if (aRank !== bRank) return aRank - bRank

    if (aRank === LONG_ROAD_RANK) {
        const streakDiff = bRoad.streak - aRoad.streak
        if (streakDiff !== 0) return streakDiff
    }

    return aIndex - bIndex
}

export function buildPanelListData(tables: Table[]): GoodRoadTableEntry[] {
    return tables
        .map((table, hallIndex) => ({
            entry: buildTableEntry(table),
            hallIndex
        }))
        .sort((a, b) =>
            compareGoodRoadSort(a.entry, b.entry, a.hallIndex, b.hallIndex)
        )
        .map(item => item.entry)
}

export const GOOD_ROAD_GAME_TYPES = ['G002', 'G003'] as const

export function isGoodRoadGameType(gameTypeNo: string): boolean {
    return gameTypeNo === 'G002' || gameTypeNo === 'G003'
}

export function filterGoodRoadGameTables(tables: Table[]): Table[] {
    return tables.filter(table => isGoodRoadGameType(table.gameTypeNo))
}

export function splitGoodRoadTablesByType(
    tables: Table[]
): Record<string, Table[]> {
    return {
        G002: tables.filter(table => table.gameTypeNo === 'G002'),
        G003: tables.filter(table => table.gameTypeNo === 'G003')
    }
}

export function countGoodRoadTables(tables: Table[]): number {
    return filterGoodRoadTables(tables).length
}

export function getMemberSideBetAmounts(
    table: Table,
    memberId: number
): { player: number; banker: number } {
    let player = 0
    let banker = 0
    const list = table.expand?.betFormList || []

    list.forEach(betInfo => {
        if (betInfo.memberId !== memberId) return

        const { playName, amount } = betInfo
        if (playName === '庄' || playName === '龙' || playName === '免佣庄') {
            banker += amount
        } else if (playName === '闲' || playName === '虎') {
            player += amount
        }
    })

    return { player, banker }
}

export function mergeTableDetail(base: Table, detail: Table): Table {
    return {
        ...base,
        ...detail,
        expand: {
            ...base.expand,
            ...detail.expand
        }
    }
}

export function mergeTableBetUpdate(base: Table, update: Table): Table {
    const updateExpand = update.expand || base.expand
    const betFormList =
        updateExpand.betFormList !== undefined &&
        updateExpand.betFormList !== null
            ? updateExpand.betFormList
            : base.expand?.betFormList ?? []

    return {
        ...base,
        expand: {
            ...base.expand,
            betFormList,
            amount:
                updateExpand.amount !== undefined &&
                updateExpand.amount !== null
                    ? updateExpand.amount
                    : base.expand?.amount,
            xianAmount: updateExpand.xianAmount ?? base.expand?.xianAmount,
            zhuangAmount:
                updateExpand.zhuangAmount ?? base.expand?.zhuangAmount,
            xianDuiAmount:
                updateExpand.xianDuiAmount ?? base.expand?.xianDuiAmount,
            zhuangDuiAmount:
                updateExpand.zhuangDuiAmount ?? base.expand?.zhuangDuiAmount,
            heAmount: updateExpand.heAmount ?? base.expand?.heAmount
        }
    }
}

export function mergeMultiDetailIntoTables(
    hallTables: Table[],
    detailTables: Table[]
): Table[] {
    if (!detailTables.length) return hallTables

    const detailById = new Map(detailTables.map(table => [table.id, table]))

    return hallTables.map(table => {
        const detail = detailById.get(table.id)
        return detail ? mergeTableDetail(table, detail) : table
    })
}

export function collectGoodRoadTableIds(
    desksByType: Record<string, Table[]>
): number[] {
    const ids = new Set<number>()

    GOOD_ROAD_GAME_TYPES.forEach(gameTypeNo => {
        const tables = desksByType[gameTypeNo] || []
        tables.forEach(table => {
            ids.add(table.id)
        })
    })

    return Array.from(ids)
}
